// SelfVisitCard — displays a single self visit (property, date, status, remark).
// Owners and admins get Edit / Delete actions.

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Home, UserCheck, Users, Pencil, Trash2, History } from "lucide-react";
import VisitActivityLog from "./VisitActivityLog";
import { formatDateTime } from "../../utils/datetime";
import "../../css/sitevisit/sitevisitcard.css";

export default function SelfVisitCard({ visit, property, index, isAdmin, onEdit, onDelete }) {
  const isAgent = visit.visit_type === "agent";
  const [showLog, setShowLog] = useState(false);

  return (
    <>
    <motion.div
      className="site-visit-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      {/* HEADER */}
      <div className="card-header">
        <div className="header-left">
          <div className="icon-circle">
            <Home size={18} />
          </div>
          <div>
            <h3>{property?.propertyName || "Property"}</h3>
            <div className="row muted">
              <MapPin size={14} />
              <span>{property?.location || "—"}</span>
            </div>
          </div>
        </div>

        <span className={`visit-type-badge ${isAgent ? "badge-agent" : "badge-direct"}`}>
          {isAgent ? <><UserCheck size={11} /> Agent</> : <><Users size={11} /> Direct</>}
        </span>
      </div>

      {/* BODY */}
      <div className="card-content">
        <div className="row">
          <Calendar size={14} />
          <span>{formatDateTime(visit.scheduled_date)}</span>
        </div>

        {/* Show agent email if agent visit */}
        {isAgent && visit.agent_email && (
          <div className="row muted">
            <UserCheck size={13} />
            <span>{visit.agent_email}</span>
          </div>
        )}

        {/* STATUS */}
        <div className="status-wrapper">
          <span className={`status-pill ${visit.status}`}>
            {visit.status}
          </span>
        </div>

        {/* FEEDBACK shown for completed visits */}
        {visit?.feedback && (
          <div className="info-block">
            <strong>Feedback:</strong>
            <p>{visit.feedback}</p>
          </div>
        )}

        {/* REMARK shown for non-completed visits */}
        {visit?.remark && (
          <div className="info-block">
            <strong>Remarks:</strong>
            <p>{visit.remark}</p>
          </div>
        )}

        {/* ACTIONS */}
        <div className="action-row">
          <button onClick={onEdit}>
            <Pencil size={13} /> Edit
          </button>
          <button onClick={onDelete}>
            <Trash2 size={13} /> Delete
          </button>
          <button className="al-trigger" onClick={() => setShowLog(true)}>
            <History size={13} /> Activity
          </button>
        </div>

        {/* FOOTER — owner email (visible to admins) */}
        {isAdmin && visit.createdByEmail && (
          <div className="created-by">
            By: <strong>{visit.createdByEmail}</strong>
          </div>
        )}
      </div>
    </motion.div>

    {showLog && (
      <VisitActivityLog log={visit.activityLog || []} onClose={() => setShowLog(false)} />
    )}
    </>
  );
}
